import React from 'react';
import { Paper, Box, Typography, Chip } from '@mui/material';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

const SHIFT_INFO = {
  MORNING: { label: 'Ca sáng', time: '7:30 - 12:00' },
  AFTERNOON: { label: 'Ca chiều', time: '13:00 - 18:30' }
};

/**
 * Generate contrasting text color based on background
 */
const getContrastColor = (hexColor) => {
  if (!hexColor) return '#333333';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#333333' : '#ffffff';
};

/**
 * ShiftRow - displays a single shift with duty type from API
 */
const ShiftRow = ({ shift, dutyType, showDivider }) => {
  const shiftInfo = SHIFT_INFO[shift];
  const bgColor = dutyType?.color || '#eeeeee';
  const textColor = getContrastColor(bgColor);

  return (
    <Box sx={{
      py: 1,
      borderTop: showDivider ? 1 : 0,
      borderColor: 'divider',
      mt: showDivider ? 1 : 0
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" color="text.secondary">
            {shiftInfo.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {shiftInfo.time}
          </Typography>
        </Box>
        <Chip
          label={dutyType?.name_vi || 'Unknown'}
          size="small"
          sx={{
            backgroundColor: bgColor,
            color: textColor,
            fontWeight: 500
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * DayScheduleCard - displays schedule for a single day
 * @param {Date} date - the date to display
 * @param {object} schedule - schedule data from API { morning, afternoon }
 * @param {boolean} isToday - whether this day is today
 */
const DayScheduleCard = ({ date, schedule, isToday: isTodayFlag }) => {
  const dayName = format(date, 'EEEE', { locale: vi });
  const dayDate = format(date, 'dd/MM');

  const hasMorning = schedule?.morning;
  const hasAfternoon = schedule?.afternoon;

  const morningIsOff = hasMorning?.duty_type?.code === 'OFF';
  const afternoonIsOff = hasAfternoon?.duty_type?.code === 'OFF';
  const isOff = (morningIsOff && afternoonIsOff) ||
                (morningIsOff && !hasAfternoon) ||
                (!hasMorning && afternoonIsOff);
  const noSchedule = !hasMorning && !hasAfternoon;

  return (
    <Paper
      sx={{
        mb: 2,
        overflow: 'hidden',
        border: isTodayFlag ? 2 : 0,
        borderColor: 'primary.main'
      }}
    >
      {/* Day Header */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: isTodayFlag ? 'primary.main' : 'grey.100',
          color: isTodayFlag ? 'white' : 'text.primary'
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
          {dayName}, {dayDate}
          {isTodayFlag && (
            <Chip
              label="Hôm nay"
              size="small"
              sx={{ ml: 1, bgcolor: 'white', color: 'primary.main' }}
            />
          )}
        </Typography>
      </Box>

      {/* Schedule Content */}
      <Box sx={{ p: 2 }}>
        {noSchedule ? (
          <Typography color="text.secondary" fontStyle="italic">
            Chưa có lịch
          </Typography>
        ) : isOff ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Chip label="NGHỈ" color="default" size="medium" />
          </Box>
        ) : (
          <>
            {/* Morning Shift */}
            {hasMorning && hasMorning.duty_type?.code !== 'OFF' && (
              <ShiftRow
                shift="MORNING"
                dutyType={hasMorning.duty_type}
              />
            )}

            {/* Afternoon Shift */}
            {hasAfternoon && hasAfternoon.duty_type?.code !== 'OFF' && (
              <ShiftRow
                shift="AFTERNOON"
                dutyType={hasAfternoon.duty_type}
                showDivider={hasMorning && hasMorning.duty_type?.code !== 'OFF'}
              />
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default DayScheduleCard;
